import { ArrowRight, Loader2 } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { grouped } from "@/entities/scene-card/model/creditRates";
import { CONTINUE_REASON_TEXT, type GroupContinueState } from "../../../model/groupContinue";

/**
 * 组图分步出图的续出条(B2-2,2026-07-31)——「照这张出剩下 N 张」。
 *
 * ═══ 它承接的是什么 ═══════════════════════════════════════════════════════
 * 组图 6 张一次预扣 1680,风格不对全废。分步之后第一步只出封面(280),用户在这里
 * **看着真图**决定要不要继续 —— 这条是那个决定的落点。
 *
 * ═══ 三条措辞纪律 ═════════════════════════════════════════════════════════
 * ① 按钮上写清**几张 + 多少积分**:这一下是真扣钱,不能只写「继续」。
 * ② 不能续时**说出原因**(封面还没出好 / 正在出图),不给不解释的灰按钮。
 * ③ 承诺只说锁得住的:「照封面的版式出」是 prompt 里真写了的(GROUP_ANCHOR_PREFIX),
 *    别写成「和封面一模一样」。
 */
export function GroupContinueBar({
  state,
  onContinue,
  busy,
}: {
  state: GroupContinueState;
  onContinue: () => void;
  busy?: boolean;
}) {
  // 不是组图 / 已经出齐 —— 整条不出现(出齐了还挂个说明只是噪音)
  if (state.reason === "notGroup" || state.reason === "allDone") return null;

  const hint = state.reason ? CONTINUE_REASON_TEXT[state.reason] : null;

  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5"
      data-group-continue={state.canContinue ? "ready" : (state.reason ?? "")}
    >
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold text-foreground">这一组还差 {state.remaining} 张</p>
        <p className="text-[12.5px] leading-relaxed text-muted-foreground">
          {hint ?? `会照着封面的版式、配色与标题位置出 —— 换的是每张的场景与大字。`}
        </p>
      </div>
      {state.canContinue && (
        <Button
          size="sm"
          disabled={busy}
          onClick={onContinue}
          className="h-9 shrink-0 gap-1.5 px-3.5 text-[13px] font-semibold"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          照这张出剩下 {state.remaining} 张
          {state.credits == null ? "" : ` · ${grouped(state.credits)} 积分`}
          {!busy && <ArrowRight className="h-3.5 w-3.5" />}
        </Button>
      )}
    </div>
  );
}
